// app_client/src/pages/admin/ProductManagement.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, Plus, Search, Edit, Trash2, 
  Box, Zap, Eye, EyeOff, AlertTriangle, ArrowDownLeft, ArrowUpRight
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import useProductStore from '../../store/useProductStore';
import Pagination from '../../components/common/Pagination';
import { useConfirmation } from '../../components/providers/ConfirmationProvider';
import useListQuery from '../../hooks/useListQuery';

const ProductManagement = () => {
  const navigate = useNavigate();
  const { confirm } = useConfirmation();
  const { 
    products, pagination, 
    loading, toggleProductStatus, deleteProduct 
  } = useProductStore();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); 
  const [statusFilter, setStatusFilter] = useState('active');
  const [page, setPage] = useState(1);

  const { refresh } = useListQuery({
    store: useProductStore,
    fetchAction: 'fetchProducts',
    filters: {
      search: searchTerm,
      type: typeFilter !== 'all' ? typeFilter : undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      limit: 15
    },
    page,
    debounceMs: 500
  });

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleStatusToggle = async (e, id, currentStatus) => {
    e.stopPropagation();
    const success = await toggleProductStatus(id, !currentStatus);
    if (success) toast.success(currentStatus ? "Ürün pasife alındı" : "Ürün aktifleştirildi");
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (await confirm("Bu ürünü silmek istediğinize emin misiniz?")) {
      const res = await deleteProduct(id);
      if (res.success) toast.success("İşlem başarılı");
      else toast.error(res.message || "Silme işlemi başarısız.", { duration: 6000 });

      if (res.success) {
        if (typeof res.nextPage === 'number' && res.nextPage !== page) {
          setPage(res.nextPage);
        } else {
          refresh();
        }
      }
    }
  };

  const formatMoney = (val, currency = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', { 
      style: 'currency', 
      currency: currency,
      minimumFractionDigits: 2 
    }).format(val || 0);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* 1. Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 bg-slate-50/50 p-4 rounded-4xl border border-slate-100">
        <div className="shrink-0">
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">Ürün & Hizmet Yönetimi</h1>
          <p className="text-slate-500 text-sm font-medium">Stoktaki malzemeler ve servis kalemleri</p>
        </div>

        <div className="flex items-center gap-3 flex-1 md:justify-end">
          <div className="relative w-full max-w-xs group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500" size={16} />
            <input 
              type="text" 
              placeholder="Ürün adı veya kod..."
              className="w-full pl-11 pr-4 py-3 rounded-2xl border border-transparent bg-white shadow-sm focus:ring-4 focus:ring-blue-500/10 outline-none transition-all text-sm font-bold"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <button onClick={() => navigate('/products/new')} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-blue-500/20 transition-all active:scale-95">
            <Plus size={18} className="lg:mr-2 inline" /> 
            <span className="hidden lg:inline">Yeni Ekle</span>
          </button>
        </div>
      </div>

      {/* 2. Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-2 p-1 bg-slate-100 w-fit rounded-2xl border border-slate-200">
          {[
            { id: 'all', label: 'Tümü', icon: Box },
            { id: 'Material', label: 'Malzeme', icon: Package },
            { id: 'Service', label: 'Hizmet', icon: Zap }
          ].map(item => (
            <button
              key={item.id}
              onClick={() => {
                setTypeFilter(item.id);
                setPage(1);
              }}
              className={`px-4 py-2 rounded-xl text-[10px] uppercase tracking-wider font-black transition-all flex items-center gap-2 ${typeFilter === item.id ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <item.icon size={14} />
              {item.label}
            </button>
          ))}
        </div>

        <select 
          className="bg-white border border-slate-200 rounded-xl px-4 py-2 text-xs font-bold text-slate-600 outline-none focus:ring-2 focus:ring-blue-500/20"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="active">Sadece Aktifler</option>
          <option value="passive">Sadece Pasifler</option>
          <option value="all">Tüm Liste</option>
        </select>
      </div>

      {/* 3. Products Table */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden relative">
        {loading && <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] z-10 flex items-center justify-center font-bold text-slate-400 text-xs uppercase tracking-widest">Veriler Güncelleniyor...</div>}
        
        <div className="overflow-x-auto min-h-100">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-50 bg-slate-50/30">
                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest w-16 text-center">Tür</th>
                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Ürün / Hizmet Detayı</th>
                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Stok Durumu</th>
                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Maliyet (Alış)</th>
                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Satış Fiyatı</th>
                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {products.length > 0 ? (
                products.map((item) => (
                  <tr 
                    key={item._id} 
                    className={`hover:bg-slate-50/80 transition-all group cursor-pointer ${!item.isActive ? 'opacity-60' : ''}`} 
                    onClick={() => navigate(`/products/${item._id}`)}
                  >
                    <td className="p-6">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.type === 'Material' ? 'bg-amber-50 text-amber-600' : 'bg-purple-50 text-purple-600'}`}>
                        {item.type === 'Material' ? <Package size={20} /> : <Zap size={20} />}
                      </div>
                    </td>
                    <td className="p-6">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="font-black text-slate-800 text-sm">{item.name}</span>
                          {!item.isActive && <span className="bg-slate-200 text-slate-600 text-[8px] px-2 py-0.5 rounded-full font-black uppercase">Pasif</span>}
                        </div>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">{item.code}</span>
                      </div>
                    </td>
                    <td className="p-6">
                      <div className="flex flex-col justify-center">
                        <span className={`text-sm font-black ${item.type === 'Material' && item.stockQuantity <= item.minStockLevel ? 'text-rose-600 flex items-center gap-1' : 'text-slate-700'}`}>
                          {item.type === 'Material' ? item.stockQuantity : '--'} 
                          {item.type === 'Material' && item.stockQuantity <= item.minStockLevel && <AlertTriangle size={14} />}
                        </span>
                        <span className="text-[10px] font-bold text-slate-400">{item.unit || 'Adet'}</span>
                      </div>
                    </td>
                    <td className="p-6">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-1 text-slate-600 font-bold text-xs">
                           <ArrowDownLeft size={12} className="text-slate-400" />
                           {formatMoney(item.purchasePrice, item.purchaseCurrency)}
                        </div>
                        <span className="text-[9px] font-black text-slate-400 uppercase">
                          {item.purchaseVatIncluded ? 'Dahil' : `+ %${item.purchaseVatRate} KDV`}
                        </span>
                      </div>
                    </td>
                    <td className="p-6">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-1 text-blue-600 font-black text-sm">
                           <ArrowUpRight size={14} className="text-blue-400" />
                           {formatMoney(item.salePrice, item.saleCurrency)}
                        </div>
                        <span className="text-[9px] font-black text-blue-400 uppercase">
                          {item.saleVatIncluded ? 'KDV Dahil' : `+ %${item.saleVatRate} KDV`}
                        </span>
                      </div>
                    </td>
                    <td className="p-6 text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        <button 
                          onClick={(e) => handleStatusToggle(e, item._id, item.isActive)} 
                          className={`p-2 rounded-xl transition-colors ${item.isActive ? 'hover:bg-slate-100 text-slate-400' : 'bg-blue-50 text-blue-600'}`}
                        >
                          {item.isActive ? <Eye size={18} /> : <EyeOff size={18} />}
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/products/${item._id}`); }} className="p-2 text-slate-600 hover:bg-slate-100 rounded-xl"><Edit size={18} /></button>
                        <button onClick={(e) => handleDelete(e, item._id)} className="p-2 text-rose-500 hover:bg-rose-50 rounded-xl"><Trash2 size={18} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : !loading && (
                <tr>
                  <td colSpan="6" className="p-20 text-center">
                    <div className="flex flex-col items-center justify-center space-y-3 opacity-40">
                      <Box size={48} className="text-slate-300" />
                      <p className="text-sm font-black text-slate-400 uppercase tracking-widest">Kayıt bulunamadı</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination 
          currentPage={page} 
          totalPages={pagination.totalPages} 
          onPageChange={handlePageChange} 
        />
      </div>
    </div>
  );
};

export default ProductManagement;
